import LinkIcon from "@mui/icons-material/Link";
import LocationOn from "@mui/icons-material/LocationOn";
import UploadIcon from "@mui/icons-material/Upload";
import {
  Collapse,
  FormControlLabel,
  Input,
  InputAdornment,
  Paper,
  Skeleton,
  Switch,
} from "@mui/material";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { CustomLink } from "core/components/CustomLink";
import { ErrorComponent } from "core/components/ErrorComponent";
import { ImageOverlay } from "core/components/ImageOverlay";
import { PageContainer } from "core/components/PageContainer";
import { PageHeader } from "core/components/PageHeader";
import { ReactHookCheckbox } from "core/components/ReactHookFormComponents/ReactHookCheckbox";
import { ReactHookDateTimeRangePicker } from "core/components/ReactHookFormComponents/ReactHookDateTimeRangePicker";
import { ReactHookTagInput } from "core/components/ReactHookFormComponents/ReactHookTagInput";
import { ReactHookTextField } from "core/components/ReactHookFormComponents/ReactHookTextField";
import { ReactHookURLInput } from "core/components/ReactHookFormComponents/ReactHookURLInput";
import { ResetButton } from "core/components/ResetButton";
import { SaveButton } from "core/components/SaveButton";
import type { TelerikHTMLEditorProps } from "core/components/TelerikHTMLEditor/TelerikHTMLEditor";
import { VisuallyHiddenInput } from "core/components/VisuallyHiddenInput";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useCentrixFetch } from "core/hooks/useCentrixFetch";
import { usePageLogging } from "core/hooks/usePageLogging";
import { useToaster } from "core/hooks/useToaster";
import { parseDateOrNull } from "core/utils/parseDateOrNull";
import {
  ACCEPTED_BANNER_FILE_TYPES,
  type BannerFormValue,
} from "features/NewsEvents/resources/NewsEventsDefs";
import { getImageSRC } from "features/NewsEvents/utils";
import { mapArticleDtoToUpdateDto } from "features/NewsEvents/utils/newsEventMappers";
import { useMarkNotificationReadByItemIdOnMount } from "features/Notifications/useMarkNotificationReadByItemIdOnMount";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { parseAsBoolean, useQueryState } from "nuqs";
import { type ChangeEventHandler, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ArticlePageLoadingSkeleton } from "../ArticlePageLoadingSkeleton";

type QuickEditFormValues = {
  content: string;
  title: string;
  banner: BannerFormValue;
  hasCTAButton: boolean;
  buttonText: string;
  buttonUrl: string;
  address: string;
  locationDescription: string;
  locationUrl: string;
  startDate?: Date | null;
  endDate?: Date | null;
  tags?: string[];
};

const defaultValues: QuickEditFormValues = {
  content: "",
  title: "",
  banner: { altText: "", description: "", bannerFile: null },
  hasCTAButton: false,
  buttonText: "",
  buttonUrl: "",
  address: "",
  locationDescription: "",
  locationUrl: "",
  startDate: null,
  endDate: null,
  tags: [],
};

const HTMLEditor = dynamic<
  TelerikHTMLEditorProps<QuickEditFormValues, "content">
>(
  () =>
    import("core/components/TelerikHTMLEditor").then(
      (mod) => mod.TelerikHTMLEditor
    ),
  {
    ssr: false,
    loading: () => <Skeleton variant="rectangular" width="100%" height={400} />,
  }
);

export function SingleEventPageEdit() {
  const [editMode, setEditMode] = useQueryState(
    "edit",
    parseAsBoolean.withDefault(false)
  );
  const {
    locale,
    query: { id },
  } = useRouter();
  const { t } = useTranslation("NewsEvents", {
    keyPrefix: "Next:NewsEvents:SingleEventPage",
  });
  useMarkNotificationReadByItemIdOnMount(id);
  usePageLogging({
    itemType: "Event",
    itemId: String(id),
    componentName: "SingleEventPage",
  });

  const { data, isError, isLoading, mutate } = useCentrixApi({
    path: "/api/news-events/articles-v2/Events/public/{id}",
    parameters: {
      path: { id: id as string },
      query: { Locale: locale! },
    },
  });
  const values = useMemo(() => {
    if (isLoading || !data) {
      return defaultValues;
    }
    const dataCopy = structuredClone(data);
    return {
      content: dataCopy?.content || "",
      title: dataCopy?.title || "",
      banner: {
        altText: dataCopy?.banner?.altText || "",
        description: dataCopy?.banner?.description || "",
        bannerFile: {
          bannerId: dataCopy?.banner?.blobName || "",
          bannerBlobDataUrl: dataCopy?.banner?.blurDataUrl || "",
          bannerBlobName: dataCopy?.banner?.blobName || "",
          bannerHeight: dataCopy?.banner?.height || 0,
          bannerWidth: dataCopy?.banner?.width || 0,
        },
      },
      hasCTAButton: Boolean(dataCopy?.buttonText && dataCopy?.buttonUrl),
      buttonText: dataCopy?.buttonText || "",
      buttonUrl: dataCopy?.buttonUrl || "",
      address: dataCopy?.address || "",
      locationDescription: dataCopy?.locationDescription || "",
      locationUrl: dataCopy?.url || "",
      startDate: parseDateOrNull(dataCopy?.startDate),
      endDate: parseDateOrNull(dataCopy?.endDate),
      tags: dataCopy?.tags || [],
    };
  }, [data, isLoading]);

  const formMethods = useForm<QuickEditFormValues>({
    defaultValues,
    values,
  });

  const { isDirty, isSubmitting } = formMethods.formState;
  const centrixFetch = useCentrixFetch();
  const { errorToast, successToast } = useToaster();

  const articleFetchErrorMessage = t("articleFetchErrorMessage");
  const translationNotFoundMessage = t("translationNotFoundMessage");
  const articleUpdateErrorMessage = t("articleUpdateErrorMessage");
  const imageUploadErrorMessage = t("imageUploadErrorMessage");
  const articleUpdateSuccessMessage = t("articleUpdateSuccessMessage");
  const [isChanging, setIsChanging] = useState(false);

  const onSubmit = formMethods.handleSubmit(async (formValues) => {
    const currentArticleRes = await centrixFetch({
      method: "get",
      path: "/api/news-events/articles-v2/{id}",
      parameters: { path: { id: id as string } },
    });
    if (!currentArticleRes.ok) {
      errorToast(articleFetchErrorMessage);
      return;
    }
    const article = await currentArticleRes.json();
    const body = mapArticleDtoToUpdateDto(article);
    const currentTranslation = body.articleTranslations?.find(
      (trans) => trans.locale === locale
    );
    if (!currentTranslation) {
      errorToast(translationNotFoundMessage);
      return;
    }

    body.address = formValues.address;
    body.locationDescription = formValues.locationDescription;
    body.url = formValues.locationUrl;
    body.startDate = formValues.startDate
      ? formValues.startDate.toISOString()
      : null;
    body.endDate = formValues.endDate ? formValues.endDate.toISOString() : null;

    currentTranslation.content = formValues.content;
    currentTranslation.title = formValues.title;
    currentTranslation.bannerId = formValues.banner.bannerFile?.bannerId || "";
    currentTranslation.hasCTA = formValues.hasCTAButton;
    currentTranslation.buttonText = formValues.hasCTAButton
      ? formValues.buttonText
      : "";
    currentTranslation.buttonUrl = formValues.buttonUrl
      ? formValues.buttonUrl
      : "";
    currentTranslation.tags = formValues.tags || [];
    currentTranslation.translationBanner = {
      id: undefined as unknown as string,
      blobName: formValues.banner.bannerFile?.bannerId ?? "",
      blurDataUrl: formValues.banner.bannerFile?.bannerBlobDataUrl ?? "",
      height: formValues.banner.bannerFile?.bannerHeight ?? 0,
      resolution: 0,
      width: formValues.banner.bannerFile?.bannerWidth ?? 0,
      altText: formValues.banner.altText,
      description: formValues.banner.description,
    };

    const updateRes = await centrixFetch({
      path: "/api/news-events/articles-v2/{id}",
      method: "put",
      parameters: { path: { id: id as string } },
      body,
    });
    if (!updateRes.ok) {
      errorToast(articleUpdateErrorMessage);
      return;
    }
    await updateRes.json(); // consume the updated article to complete the request
    await mutate();
    formMethods.reset();
    setEditMode(false);
    successToast(articleUpdateSuccessMessage);
    // Submit updated content to the server
  });

  const onChange: ChangeEventHandler<HTMLInputElement> = async (e) => {
    setIsChanging(true);
    const file = e.target.files?.[0];
    if (!file) {
      setIsChanging(false);
      return;
    }
    const response = await centrixFetch({
      path: "/api/news-events/articles-v2/upload",
      method: "post",
      contentType: "multipart/form-data",
      body: { file: file as unknown as string },
    });
    if (!response.ok) {
      errorToast(imageUploadErrorMessage);
      setIsChanging(false);
      return;
    }
    const bannerData = await response.json();
    if (!bannerData) {
      setIsChanging(false);
      return;
    }
    formMethods.setValue("banner.bannerFile", bannerData, {
      shouldDirty: true,
    });
    setIsChanging(false);
  };

  if (isError) {
    return (
      <PageContainer>
        <ErrorComponent apiError={isError} />
      </PageContainer>
    );
  }
  if (isLoading || !data) {
    return <ArticlePageLoadingSkeleton />;
  }

  const { banner, articleCategory, title } = data;

  const blurDataUrl = banner?.blurDataUrl ?? "";
  const altText = banner?.altText ?? "";
  const [blobName, width, height] = formMethods.watch([
    "banner.bannerFile.bannerId",
    "banner.bannerFile.bannerWidth",
    "banner.bannerFile.bannerHeight",
  ]);

  const imageSRC = blobName ? getImageSRC(blobName) : undefined;

  return (
    <PageContainer maxWidth="md">
      <PageHeader
        TopSlot={
          <Breadcrumbs separator=">">
            <CustomLink href="/events">{t("eventsLink")}</CustomLink>
            {articleCategory ? (
              <CustomLink href={`/events?categories=${articleCategory.id}`}>
                {articleCategory.title}
              </CustomLink>
            ) : (
              <CustomLink href="/events?categories=uncategorized">
                {t("uncategorized")}
              </CustomLink>
            )}
            <span />
          </Breadcrumbs>
        }
        TitleSlot={
          <Input
            sx={{ fontSize: "2.75rem", fontWeight: "400", width: "100%" }}
            inputProps={{
              ...formMethods.register("title"),
            }}
          />
        }
        hideTitle
        RightSlot={
          <Stack alignItems="end">
            <FormControlLabel
              control={<Switch />}
              checked={editMode}
              onChange={(_, checked) => setEditMode(checked)}
              labelPlacement="start"
              label={t("editToggle", "Quick Edit")}
            />
            <Tooltip
              title={t("editToggleTooltipTitle", "Go to full edit page")}
              placement="right"
            >
              <CustomLink href={`/manage/publications/event-article/${id}`}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LinkIcon />
                  {t("edit", "Full Edit")}
                </Stack>
              </CustomLink>
            </Tooltip>
            <Stack direction="row" spacing={1} mt={2}>
              <ResetButton
                size="small"
                onClick={() => formMethods.reset()}
                disabled={!isDirty}
              />
              <SaveButton
                size="small"
                disabled={!isDirty}
                onClick={onSubmit}
                loading={isSubmitting}
              />
            </Stack>
          </Stack>
        }
        title={title ?? ""}
        titleStackProps={{ mb: 1 }}
      />

      <Stack spacing={2} mb={2}>
        <ReactHookDateTimeRangePicker
          required
          control={formMethods.control}
          slotProps={{ textField: { size: "small" } }}
          fromName="startDate"
          toName="endDate"
          fromLabel={t("startDate", "Event Start Date")}
          toLabel={t("endDate", "Event End Date")}
        />
        <ReactHookTextField
          label={t("address", "Address")}
          control={formMethods.control}
          name="address"
          textFieldProps={{
            size: "small",
            InputProps: {
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOn fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <ReactHookTextField
          fullWidth
          textFieldProps={{ size: "small" }}
          label={t("locationDescription", "Location Description")}
          control={formMethods.control}
          name="locationDescription"
        />
        <ReactHookURLInput
          fullWidth
          textFieldProps={{ size: "small" }}
          label={t("locationUrl", "Location URL")}
          control={formMethods.control}
          name="locationUrl"
        />
      </Stack>
      <ImageOverlay
        component="label"
        imageSrc={imageSRC ?? ""}
        altText={altText}
        blurDataUrl={blurDataUrl}
        height={height ?? 0}
        width={width ?? 0}
        overlaySlot={
          <Paper
            sx={{
              p: 1,
              backgroundColor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <UploadIcon />
              <Typography variant="button" lineHeight={0}>
                {t("upload", "Upload")}
              </Typography>
            </Stack>
          </Paper>
        }
      >
        <VisuallyHiddenInput
          type="file"
          onChange={onChange}
          disabled={isChanging}
          sx={{ gridArea: "stack" }}
          accept={ACCEPTED_BANNER_FILE_TYPES}
        />
      </ImageOverlay>
      <Stack my={4} spacing={4} direction="row">
        <ReactHookTextField
          label={t("bannerAltText", "Banner Alt Text")}
          fullWidth
          required
          control={formMethods.control}
          textFieldProps={{ variant: "standard" }}
          name="banner.altText"
        />
        <ReactHookTextField
          label={t("bannerDescription", "Banner Description")}
          required
          fullWidth
          control={formMethods.control}
          textFieldProps={{ variant: "standard" }}
          name="banner.description"
        />
      </Stack>
      <Divider sx={{ my: 4 }} />
      <HTMLEditor
        disableLabel
        label="content"
        required
        control={formMethods.control}
        name="content"
        style={{ marginBlockEnd: "2rem" }}
      />
      <ReactHookCheckbox
        control={formMethods.control}
        name="hasCTAButton"
        label={t("hasCTAButton", "Call-To-Action Button Enabled")}
      />
      <Collapse in={formMethods.watch("hasCTAButton")}>
        <Stack direction="row" spacing={4} mt={2}>
          <ReactHookTextField
            label={t("buttonText", "Button Text")}
            fullWidth
            required={formMethods.watch("hasCTAButton")}
            control={formMethods.control}
            name="buttonText"
          />
          <ReactHookURLInput
            label={t("buttonUrl", "Button URL")}
            fullWidth
            required={formMethods.watch("hasCTAButton")}
            control={formMethods.control}
            name="buttonUrl"
          />
        </Stack>
      </Collapse>
      <Divider sx={{ my: 4 }} />
      <ReactHookTagInput
        control={formMethods.control}
        fullWidth
        name="tags"
        label={t("tags", "Tags")}
      />
    </PageContainer>
  );
}
